import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import { useSnackbar } from "notistack";
import axiosDonation from "../../../apis/axiosDonation";
import { API_URL_DONATION } from "../../../utils/setting";
import donationApi from "../../../apis/donationApi";

interface ModalCompleteOrderProps {
  open: boolean;
  handleClose: () => void;
  order: any;
  onSuccess: () => void;
}

const ModalCompleteOrder: React.FC<ModalCompleteOrderProps> = ({
  open,
  handleClose,
  order,
  onSuccess,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [pickupImage, setPickupImage] = useState<string>("");
  const [deliveryImage, setDeliveryImage] = useState<string>("");
  const [isUploadingPickup, setIsUploadingPickup] = useState(false);
  const [isUploadingDelivery, setIsUploadingDelivery] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "pickup" | "delivery"
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (type === "pickup") setIsUploadingPickup(true);
    else setIsUploadingDelivery(true);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await axiosDonation.post("/serene/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const relativeUrl = res.data?.data?.url;
      const baseUrl = (API_URL_DONATION || "").replace(/\/$/, "");
      const fullUrl = relativeUrl ? `${baseUrl}${relativeUrl}` : "";

      if (fullUrl) {
        if (type === "pickup") {
          setPickupImage(fullUrl);
        } else {
          setDeliveryImage(fullUrl);
        }
        enqueueSnackbar("Tải ảnh lên thành công", { variant: "success" });
      } else {
        throw new Error("Không nhận được URL ảnh");
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Lỗi khi tải ảnh lên, vui lòng thử lại", { variant: "error" });
    } finally {
      if (type === "pickup") setIsUploadingPickup(false);
      else setIsUploadingDelivery(false);
    }
  };

  const handleSubmit = async () => {
    if (!pickupImage && !deliveryImage) {
      enqueueSnackbar("Vui lòng tải lên ít nhất 1 ảnh (ảnh nhận hàng hoặc ảnh trả hàng)", {
        variant: "warning",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Save delivery pictures locally/session for synchronization and audit
      try {
        const storedProof = JSON.parse(localStorage.getItem("order_delivery_proofs") || "{}");
        storedProof[order.id] = {
          orderId: order.id,
          orderCode: order.code,
          pickupImage: pickupImage,
          deliveryImage: deliveryImage,
          completedAt: new Date().toISOString(),
          driverId: localStorage.getItem("driverId") || order.driverId,
          driverName: localStorage.getItem("driverName") || order.driverName,
        };
        localStorage.setItem("order_delivery_proofs", JSON.stringify(storedProof));
      } catch (e) {
        console.error("Save proof error", e);
      }

      // Call finish donation API
      const res = await donationApi.doneDonation({ id: order.id });
      if (res?.data?.data || res?.data?.code === 0) {
        enqueueSnackbar("Hoàn thành đơn hàng thành công!", { variant: "success" });
        onSuccess();
        handleClose();
      } else {
        enqueueSnackbar(res.data.message || "Không thể cập nhật hoàn thành đơn", {
          variant: "error",
        });
      }
    } catch (err: any) {
      console.error(err);
      enqueueSnackbar(err.response?.data?.message || "Lỗi khi hoàn tất đơn hàng", {
        variant: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle style={{ fontWeight: "bold", borderBottom: "1px solid #f0f0f0" }}>
        Xác nhận hoàn thành đơn hàng #{order?.code}
      </DialogTitle>
      <DialogContent dividers>
        <Box style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Label 1: Nhận hàng */}
          <div>
            <Typography variant="subtitle1" style={{ fontWeight: 600, color: "#1e293b", marginBottom: 6 }}>
              📸 1. Nhận hàng (Ảnh lúc nhận hàng từ khách)
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 10 }}>
              Chụp hoặc tải lên hình ảnh túi đồ / hàng hóa khi nhận từ khách hàng.
            </Typography>

            {pickupImage ? (
              <Box
                style={{
                  position: "relative",
                  width: "100%",
                  height: 180,
                  borderRadius: 8,
                  overflow: "hidden",
                  border: "2px solid #4caf50",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#f8fafc",
                }}
              >
                <img
                  src={pickupImage}
                  alt="Ảnh nhận hàng"
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
                <IconButton
                  onClick={() => setPickupImage("")}
                  style={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    backgroundColor: "rgba(0,0,0,0.6)",
                    color: "#fff",
                  }}
                  size="small"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <label
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 120,
                  border: "2px dashed #94a3b8",
                  borderRadius: 8,
                  cursor: isUploadingPickup ? "not-allowed" : "pointer",
                  backgroundColor: "#f8fafc",
                }}
              >
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  style={{ display: "none" }}
                  onChange={(e) => handleUpload(e, "pickup")}
                  disabled={isUploadingPickup}
                />
                {isUploadingPickup ? (
                  <CircularProgress size={28} color="success" />
                ) : (
                  <>
                    <AddPhotoAlternateIcon style={{ fontSize: 32, color: "#16a34a", marginBottom: 4 }} />
                    <span style={{ fontSize: 13, color: "#334155", fontWeight: 500 }}>
                      Nhấn để chụp / tải ảnh nhận hàng
                    </span>
                  </>
                )}
              </label>
            )}
          </div>

          {/* Label 2: Trả hàng */}
          <div>
            <Typography variant="subtitle1" style={{ fontWeight: 600, color: "#1e293b", marginBottom: 6 }}>
              📦 2. Trả hàng (Ảnh shipper bỏ hàng tại điểm tập kết / kho)
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 10 }}>
              Chụp hoặc tải lên hình ảnh hàng hóa đã giao tại điểm tập kết / kho REshare.
            </Typography>

            {deliveryImage ? (
              <Box
                style={{
                  position: "relative",
                  width: "100%",
                  height: 180,
                  borderRadius: 8,
                  overflow: "hidden",
                  border: "2px solid #4caf50",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#f8fafc",
                }}
              >
                <img
                  src={deliveryImage}
                  alt="Ảnh trả hàng"
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
                <IconButton
                  onClick={() => setDeliveryImage("")}
                  style={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    backgroundColor: "rgba(0,0,0,0.6)",
                    color: "#fff",
                  }}
                  size="small"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <label
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 120,
                  border: "2px dashed #94a3b8",
                  borderRadius: 8,
                  cursor: isUploadingDelivery ? "not-allowed" : "pointer",
                  backgroundColor: "#f8fafc",
                }}
              >
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  style={{ display: "none" }}
                  onChange={(e) => handleUpload(e, "delivery")}
                  disabled={isUploadingDelivery}
                />
                {isUploadingDelivery ? (
                  <CircularProgress size={28} color="success" />
                ) : (
                  <>
                    <AddPhotoAlternateIcon style={{ fontSize: 32, color: "#2563eb", marginBottom: 4 }} />
                    <span style={{ fontSize: 13, color: "#334155", fontWeight: 500 }}>
                      Nhấn để chụp / tải ảnh trả hàng tại điểm tập kết
                    </span>
                  </>
                )}
              </label>
            )}
          </div>
        </Box>
      </DialogContent>
      <DialogActions style={{ padding: "12px 20px" }}>
        <Button onClick={handleClose} color="inherit" disabled={isSubmitting}>
          Hủy
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="success"
          disabled={isSubmitting || isUploadingPickup || isUploadingDelivery}
        >
          {isSubmitting ? <CircularProgress size={22} color="inherit" /> : "Xác nhận hoàn thành"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalCompleteOrder;
